import { UploadFile } from 'antd';
import { Dayjs } from 'dayjs';
import { FicheDeLiaison } from 'types/User';

export type RAI = '3.1' | '3.2';

export enum VehiculeType {
  LEGER = 'Véhicule léger ou utilitaire léger (M1, N1, N2 avec dérogation de poids)',
  LOURD = 'Véhicule lourd (N2 ou N3)',
  BUS = 'Autocar ou autobus (M2 ou M3)',
}

export enum CategorieVehicule {
  M1 = 'M1',
  N1 = 'N1',
  N2 = 'N2 sous dérogation de poids',
  VUL_MOINS_1_55 = 'VUL neuf N1 ou N2 bénéficiant de la dérogation du poids (≤ 1,55 tonne)',
  VUL_ENTRE_1_55_ET_2 = 'VUL neuf N1 ou N2 bénéficiant de la dérogation du poids (> 1,55 tonne et ≤ 2 tonnes)',
  VUL_PLUS_2 = 'VUL neuf N1 ou N2 bénéficiant de la dérogation du poids (> 2 tonnes)',
}

export enum CategorieVehiculeLourd {
  CAMION_3_5_A_4_25 = 'Camion porteur > 3,5 tonnes et < 4,25 tonnes',
  CAMION_4_25_A_7_5 = 'Camion porteur ≥ 4,25 tonnes et < 7,5 tonnes',
  CAMION_7_5_A_12 = 'Camion porteur ≥ 7,5 tonnes et ≤ 12 tonnes',
  CAMION_12_A_19 = 'Camion porteur ≥ 12 tonnes et <19 tonnes',
  CAMION_19_A_26 = 'Camion porteur ≥ 19 tonnes et < 26 tonnes',
  CAMION_26_ET_TRACTEUR = 'Camion porteur ≥ 26 tonnes et tracteur routier',
  BENNE_AGGLO_MOINS_250 = 'Benne à ordures ménagères (agglomération ≤ 250 000 habitants)',
  BENNE_AGGLO_PLUS_250 = 'Benne à ordures ménagères (agglomération > 250 000 habitants)',
}

export enum CategorieVehiculeBus {
  BUS_STANDARD_AGGLO_MOINS_250 = 'Autobus standard (agglomération ≤ 250 000 habitants)',
  BUS_STANDARD_AGGLO_PLUS_250 = 'Autobus standard (agglomération > 250 000 habitants)',
  AUTOCAR_STANDARD_ENTREPRISE = 'Autocar standard (autre personne morale)',
  BUS_GRANDE_CAPA_AGGLO_MOINS_250 = 'Autobus grande capacité (agglomération ≤ 250 000 habitants)',
  BUS_GRANDE_CAPA_AGGLO_PLUS_250 = 'Autobus grande capacité (agglomération > 250 000 habitants)',
  AUTOCAR_GRANDE_CAPA_ENTREPRISE = 'Autocar grande capacité (autre personne morale)',
  BUS_RETROFIT_AGGLO_MOINS_250 = "Autobus issu d'une opération de rétrofit (agglomération ≤ 250 000 habitants)",
  BUS_RETROFIT_AGGLO_PLUS_250 = "Autobus issu d'une opération de rétrofit (agglomération > 250 000 habitants)",
  AUTOCAR_RETROFIT = "Autocar issu d'une opération de rétrofit",
}

export enum ClientType {
  PERSONNE_PHYSIQUE = 'Personne physique',
  PERSONNE_MORALE = 'Personne morale',
}

export enum statutPrecarite {
  CLASSIQUE = 'Classique',
  MODESTE = 'Modeste',
  PRECAIRE = 'Précaire',
}

export enum YesOrNo {
  OUI = 'Oui',
  NON = 'Non',
}

export enum UsageVehicule {
  FONCTION = 'fonction',
  LOCATION_COURTE = 'location_courte',
  LOCATION_LONGUE = 'location_longue',
}

export enum UsageVehiculeParticulier {
  ACHAT = 'Achat d’un ou plusieurs véhicules légers ou véhicules utilitaires légers électriques neufs',
  LOCATION = 'Location d’un ou plusieurs véhicules légers ou véhicules utilitaires légers électriques neufs',
  RETROFIT_LEGER = 'Rétrofit électrique d’un ou plusieurs véhicules légers',
  RETROFIT_UTILITAIRE = 'Rétrofit électrique d’un ou plusieurs véhicules utilitaires légers',
}

export enum UsageVehiculeLourd {
  ACHAT = "Achat d'un ou plusieurs véhicules lourds électriques neufs",
  LOCATION = "Location d'un ou plusieurs véhicules lourds électriques neufs",
  RETROFIT = "Rétrofit électrique d'un ou plusieurs véhicules lourds électriques neufs",
}

export enum UsageAutobus {
  ACHAT = "Achat d'un ou plusieurs autocars ou autobus électriques neufs",
  LOCATION = "Location d'un ou plusieurs autocars ou autobus électriques neufs",
  RETROFIT = "Rétrofit électrique d'un ou plusieurs autocars ou autobus électriques neufs",
}

export enum TypeClientLourd {
  AGGLO_MOINS_250 = 'Agglomération ≤ 250 000 habitants',
  AGGLO_PLUS_250 = 'Agglomération > 250 000 habitants',
  AUTRE = 'Autre personne morale',
}
export enum TypeStructure {
  Association = 'association',
  BailleurSocial = 'bailleur_social',
  CollectiviteTerritoriale = 'collectivite_territoriale',
  PromoteurPrive = 'promoteur_prive',
  SyndicDeCopropriete = 'syndic_de_copropriete',
  SCI = 'sci',
  EntreprisePrivee = 'entreprise_privee',
  Autre = 'autre',
}

export const usageParticulierParCategories: Partial<
  Record<CategorieVehicule, UsageVehiculeParticulier[]>
> = {
  [CategorieVehicule.M1]: [
    UsageVehiculeParticulier.ACHAT,
    UsageVehiculeParticulier.LOCATION,
    UsageVehiculeParticulier.RETROFIT_LEGER,
  ],
  [CategorieVehicule.N1]: [
    UsageVehiculeParticulier.ACHAT,
    UsageVehiculeParticulier.LOCATION,
    UsageVehiculeParticulier.RETROFIT_UTILITAIRE,
  ],
  [CategorieVehicule.N2]: [
    UsageVehiculeParticulier.ACHAT,
    UsageVehiculeParticulier.LOCATION,
    UsageVehiculeParticulier.RETROFIT_UTILITAIRE,
  ],
  [CategorieVehicule.VUL_MOINS_1_55]: [
    UsageVehiculeParticulier.ACHAT,
    UsageVehiculeParticulier.LOCATION,
    UsageVehiculeParticulier.RETROFIT_UTILITAIRE,
  ],
  [CategorieVehicule.VUL_ENTRE_1_55_ET_2]: [
    UsageVehiculeParticulier.ACHAT,
    UsageVehiculeParticulier.LOCATION,
    UsageVehiculeParticulier.RETROFIT_UTILITAIRE,
  ],
  [CategorieVehicule.VUL_PLUS_2]: [
    UsageVehiculeParticulier.ACHAT,
    UsageVehiculeParticulier.LOCATION,
    UsageVehiculeParticulier.RETROFIT_UTILITAIRE,
  ],
};

export const usageLourdParCategories: Record<
  CategorieVehiculeLourd | CategorieVehiculeBus,
  UsageVehiculeLourd[] | UsageAutobus[]
> = {
  [CategorieVehiculeLourd.CAMION_3_5_A_4_25]: [
    UsageVehiculeLourd.ACHAT,
    UsageVehiculeLourd.LOCATION,
    UsageVehiculeLourd.RETROFIT,
  ],
  [CategorieVehiculeLourd.CAMION_4_25_A_7_5]: [
    UsageVehiculeLourd.ACHAT,
    UsageVehiculeLourd.LOCATION,
    UsageVehiculeLourd.RETROFIT,
  ],
  [CategorieVehiculeLourd.CAMION_7_5_A_12]: [
    UsageVehiculeLourd.ACHAT,
    UsageVehiculeLourd.LOCATION,
    UsageVehiculeLourd.RETROFIT,
  ],
  [CategorieVehiculeLourd.CAMION_12_A_19]: [
    UsageVehiculeLourd.ACHAT,
    UsageVehiculeLourd.LOCATION,
    UsageVehiculeLourd.RETROFIT,
  ],
  [CategorieVehiculeLourd.CAMION_19_A_26]: [
    UsageVehiculeLourd.ACHAT,
    UsageVehiculeLourd.LOCATION,
    UsageVehiculeLourd.RETROFIT,
  ],
  [CategorieVehiculeLourd.CAMION_26_ET_TRACTEUR]: [
    UsageVehiculeLourd.ACHAT,
    UsageVehiculeLourd.LOCATION,
    UsageVehiculeLourd.RETROFIT,
  ],
  [CategorieVehiculeLourd.BENNE_AGGLO_MOINS_250]: [
    UsageVehiculeLourd.ACHAT,
    UsageVehiculeLourd.LOCATION,
    UsageVehiculeLourd.RETROFIT,
  ],
  [CategorieVehiculeLourd.BENNE_AGGLO_PLUS_250]: [
    UsageVehiculeLourd.ACHAT,
    UsageVehiculeLourd.LOCATION,
    UsageVehiculeLourd.RETROFIT,
  ],
  [CategorieVehiculeBus.BUS_STANDARD_AGGLO_MOINS_250]: [
    UsageAutobus.ACHAT,
    UsageAutobus.LOCATION,
    UsageAutobus.RETROFIT,
  ],
  [CategorieVehiculeBus.BUS_STANDARD_AGGLO_PLUS_250]: [
    UsageAutobus.ACHAT,
    UsageAutobus.LOCATION,
    UsageAutobus.RETROFIT,
  ],
  [CategorieVehiculeBus.AUTOCAR_STANDARD_ENTREPRISE]: [
    UsageAutobus.ACHAT,
    UsageAutobus.LOCATION,
    UsageAutobus.RETROFIT,
  ],
  [CategorieVehiculeBus.BUS_GRANDE_CAPA_AGGLO_MOINS_250]: [
    UsageAutobus.ACHAT,
    UsageAutobus.LOCATION,
    UsageAutobus.RETROFIT,
  ],
  [CategorieVehiculeBus.BUS_GRANDE_CAPA_AGGLO_PLUS_250]: [
    UsageAutobus.ACHAT,
    UsageAutobus.LOCATION,
    UsageAutobus.RETROFIT,
  ],
  [CategorieVehiculeBus.AUTOCAR_GRANDE_CAPA_ENTREPRISE]: [
    UsageAutobus.ACHAT,
    UsageAutobus.LOCATION,
    UsageAutobus.RETROFIT,
  ],
  [CategorieVehiculeBus.BUS_RETROFIT_AGGLO_MOINS_250]: [UsageAutobus.RETROFIT],
  [CategorieVehiculeBus.BUS_RETROFIT_AGGLO_PLUS_250]: [UsageAutobus.RETROFIT],
  [CategorieVehiculeBus.AUTOCAR_RETROFIT]: [UsageAutobus.RETROFIT],
};

export interface NouveauDossierConcessionnaireBase {
  typeClientFinal: ClientType;
  vehiculeType?: VehiculeType;
  typeClientLourd?: TypeClientLourd;
  statutPrecarite: statutPrecarite;
  avisImposition: UploadFile[];
  scoreEnvironnementalAtteint: YesOrNo;
  coutAcquisitionInferieur47000: YesOrNo;
  masseVehiculeInferieure2400: YesOrNo;
  dejaBeneficieBonusEcologique: YesOrNo;
  produitEnEurope?: YesOrNo;
  vehiculeAdeme: YesOrNo;
  adresse: string;
  ville: string;
  codePostal: string;
  fonction: string;
  typeStructure?: TypeStructure;
  typeAcheteur:
    | 'company_less_100'
    | 'company_more_100'
    | 'org_less_20'
    | 'org_more_20'
    | 'loueur_ou_vendeur'
    | 'Personne morale';
  nbDevis: string;
  dateDevis?: Dayjs;
  dateEditionDevis?: Dayjs;
  datePrevisionnelleDevis?: Dayjs;
  dateLivraison: Dayjs;
  email: string;
  raisonSociale: string;
  etablissement: string;
  firstname: string;
  lastname: string;
  phone: string;
  siret: string;
  nbVehicules: number;
  accountId?: string;
  usageVehicules?: UsageVehicule | UsageVehiculeLourd | UsageAutobus;
  usageVehiculeParticulier: UsageVehiculeParticulier;
  categorieVehicule:
    | CategorieVehicule
    | CategorieVehiculeLourd
    | CategorieVehiculeBus;
  repartition: number;
  primeParVehicule: number;
  primeParGwh: number;
  volume: number;
  isVehiculeDemonstration?: boolean;
  ficheDeLiaison?: FicheDeLiaison;
}

export interface NouveauDossierConcessionnaireData extends NouveauDossierConcessionnaireBase {
  devis: UploadFile[];
  otherFiles: UploadFile[];
}

export interface NouveauDossierConcessionnaireParams extends Omit<
  NouveauDossierConcessionnaireBase,
  | 'dateEditionDevis'
  | 'dateDevis'
  | 'dateLivraison'
  | 'datePrevisionnelleDevis'
  | 'scoreEnvironnementalAtteint'
  | 'coutAcquisitionInferieur47000'
  | 'masseVehiculeInferieure2400'
  | 'dejaBeneficieBonusEcologique'
  | 'produitEnEurope'
  | 'vehiculeAdeme'
  | 'ficheDeLiaison'
> {
  dateEditionDevis?: string;
  dateDevis?: string;
  dateLivraison: string;
  datePrevisionnelleDevis?: string;
  scoreEnvironnementalAtteint?: boolean;
  coutAcquisitionInferieur47000?: boolean;
  masseVehiculeInferieure2400?: boolean;
  dejaBeneficieBonusEcologique?: boolean;
  produitEnEurope?: boolean;
  vehiculeAdeme?: boolean;
  ficheDeLiaisonId?: string;
  rai?: RAI;
}

export type CalculPrimeConcessionnaireParams = Pick<
  NouveauDossierConcessionnaireBase,
  'typeAcheteur' | 'categorieVehicule' | 'typeClientFinal' | 'usageVehicules'
> & { ficheDeLiaisonId: string };

export type CalculPrimeParticulierConsessionnaireParams = {
  typeClientFinal: ClientType;
  categorieVehicule: CategorieVehicule;
  scoreEnvironnementalAtteint?: boolean;
  coutAcquisitionInferieur47000?: boolean;
  masseVehiculeInferieure2400?: boolean;
  dejaBeneficieBonusEcologique?: boolean;
  produitEnEurope?: boolean;
  vehiculeAdeme?: boolean;
  datePrevisionnelleDevis: Dayjs;
  codePostal: string;
  usageVehiculeParticulier: UsageVehiculeParticulier;
  statutPrecarite: statutPrecarite;
};

export type PrimeBuyerType =
  | 'company_less_100'
  | 'company_more_100'
  | 'org_less_20'
  | 'org_more_20'
  | 'loueur_ou_vendeur'
  | 'Personne morale'
  | 'agglo_less_250'
  | 'agglo_more_250'
  | 'other';

export interface PrimeMontant {
  primeParVehicule: number | null;
  primeParGwh: number | null;
}

export enum GestionPrimeRole {
  CONCESSIONNAIRE = 'Concessionnaire',
  APPORTEUR_D_AFFAIRES = 'Apporteur d’affaires',
}

export enum GestionPrimeTypeEntite {
  PARC_TOTAL_SUP_100 = 'Parc total supérieur à 100 véhicules',
  PARC_TOTAL_INF_100 = 'Parc total inférieur à 100 véhicules',
  PARC_SUP_20 = 'Parc supérieur à 20 véhicules',
  PARC_INF_20 = 'Parc inférieur à 20 véhicules',
  AGGLO_MOINS_250 = 'Agglomération ≤ 250 000 habitants',
  AGGLO_PLUS_250 = 'Agglomération > 250 000 habitants',
  AUTRE_PERSONNE_MORALE_HORS_AGGLO = 'Autre personne morale (Hors agglomération)',
  PERSONNE_MORALE = 'Personne morale',
}

export enum GestionPrimeTypeVehicule {
  VL_NEUF_M1 = 'VL neuf M1',
  VUL_NEUF_N1_OU_N2 = 'VUL neuf N1 ou N2 bénéficiant de la dérogation de poids',
  VUL_NEUF_N1_OU_N2_MOINS_1_55 = 'VUL neuf N1 ou N2 bénéficiant de la dérogation du poids (≤ 1,55 tonne)',
  VUL_NEUF_N1_OU_N2_ENTRE_1_55_ET_2 = 'VUL neuf N1 ou N2 bénéficiant de la dérogation du poids (> 1,55 tonne et ≤ 2 tonnes)',
  VUL_NEUF_N1_OU_N2_PLUS_2 = 'VUL neuf N1 ou N2 bénéficiant de la dérogation du poids (> 2 tonnes)',
  VL_RETROFIT_M1 = 'VL M1 issu d’une opération de rétrofit',
  VUL_RETROFIT_N1_OU_N2 = 'VUL N1 ou N2 bénéficiant de la dérogation de poids issu d’une opération de rétrofit',
  AUTOCAR_RETROFIT = "Autocar issu d'une opération de rétrofit",
  AUTOCAR_STANDARD = 'Autocar standard',
  AUTOCAR_GRANDE_CAPACITE = 'Autocar grande capacité',
  AUTOBUS_RETROFIT = 'Autobus issu d’une opération de rétrofit',
  AUTOBUS_STANDARD = 'Autobus standard',
  AUTOBUS_GRANDE_CAPACITE = 'Autobus grande capacité',
  CAMION_3_5_A_4_25 = 'Camion porteur – 3,5 t à 4,25 t',
  CAMION_4_25_A_7_5 = 'Camion porteur – 4,25 t à 7,5 t',
  CAMION_7_5_A_12 = 'Camion porteur – 7,5 t à 12 t',
  CAMION_12_A_19 = 'Camion porteur – 12 t à 19 t',
  CAMION_19_A_26 = 'Camion porteur – 19 t à 26 t',
  CAMION_26_ET_TRACTEUR = 'Camion porteur – ≥ 26 t / Tracteur',
  BENNE_A_ORDURES_MENAGERES = 'Benne à ordures ménagères',
}

export interface GestionPrimeConcessionnaire {
  id: string;
  createdDate: string;
  role: GestionPrimeRole | null;
  ficheDeLiaisonId: string;
  fostId: string | null;
  fostName: string | null;
  typeEntite: GestionPrimeTypeEntite | null;
  typeVehicule: GestionPrimeTypeVehicule | null;
  montantPrimeHt: number | null;
}

export type PrimeBuyerMap = Partial<Record<PrimeBuyerType, PrimeMontant>>;

export type PrimesConcessionnaireCategory =
  | CategorieVehicule
  | CategorieVehiculeLourd
  | CategorieVehiculeBus;

export type PrimesConcessionnaire = Partial<
  Record<PrimesConcessionnaireCategory, PrimeBuyerMap>
>;

export interface ConcessionnaireFormAHData {
  reference: string;
  date: Dayjs | null;
  dateDevis?: Dayjs | null;
  bonCommande: UploadFile[];
  facture: UploadFile[];
  certificat: UploadFile[];
  autresDocuments?: UploadFile[];
  attestationDeTransformation?: UploadFile[];
  premierCertificatImmatriculation?: UploadFile[];
  recepisseFinDemonstration?: UploadFile[];
  immatriculation: string;
  identification: string;
  typeVarianteVersion: string;
  modeleVehicule: string;
  lastname: string;
  firstname: string;
  telephone: string;
  email: string;
}

export interface AttestationHonneurSignataireFormData {
  lastname: string;
  firstname: string;
  title: string;
  email: string;
  phone: string;
}

export interface GenerationAHSignatairesData {
  dateMiseEnService: Dayjs;
  beneficiaireSignataire: AttestationHonneurSignataireFormData;
  professionnelSignataire: AttestationHonneurSignataireFormData;
  gestionnaireReseauSignataire: AttestationHonneurSignataireFormData;
}

export type GenerationAHFormData =
  | ConcessionnaireFormAHData
  | GenerationAHSignatairesData;
